import React, { useState, useEffect } from "react";
import { FaTimes, FaMapMarkerAlt, FaCheck, FaSpinner } from "react-icons/fa";
import useBodyScrollLock from "../../../../utils/useBodyScrollLock";
import usePincodeCheck from "../../../../utils/usePincodeCheck";
import usePincodeLocation from "../../../../utils/usePincodeLocation";
import {
  ADDR_REQUIRED,
  MAX_ADDRESS_LENGTH,
  validateAddr,
  ADDRESS_TYPES,
} from "../utils/addressValidation";

const CheckoutEditModal = ({ address, loading, onSubmit, onCancel }) => {
  // Lock background body scroll while the modal is open
  useBodyScrollLock(true);

  const [form, setForm] = useState({
    name: address?.name || "",
    mobile: address?.mobile || address?.phone || "",
    email: address?.email || "",
    gstin: address?.gstin || address?.gst_number || "",
    address_type: address?.address_type || address?.type || "Home",
    address: address?.address || address?.street || "",
    city: address?.city || "",
    state: address?.state || "",
    pincode: address?.pincode || "",
    latitude: address?.latitude ?? "",
    longitude: address?.longitude ?? "",
    is_default: Boolean(address?.is_default),
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showCoordinates, setShowCoordinates] = useState(
    Boolean(address?.latitude || address?.longitude)
  );

  // Dynamic pincode serviceability check
  const { status: pincodeStatus, data: pincodeData, checkedPincode } = usePincodeCheck(form.pincode);
  const pincodeLocation = usePincodeLocation(form.pincode);
  const locationMatchesPincode = pincodeLocation?.pincode === String(form.pincode || '').trim();
  const backendResultMatchesPincode = checkedPincode === String(form.pincode || '').trim();
  const pincodeCity = (pincodeStatus === 'serviceable' && backendResultMatchesPincode && (pincodeData?.city_name || pincodeData?.city)) ||
    (locationMatchesPincode ? pincodeLocation.city : '');
  const pincodeState = (pincodeStatus === 'serviceable' && backendResultMatchesPincode && (pincodeData?.state || pincodeData?.state_name)) ||
    (locationMatchesPincode ? pincodeLocation.state : '');

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !loading) {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [loading, onCancel]);

  // Prefer location details returned with a successful backend serviceability check.
  useEffect(() => {
    if (
      (pincodeCity && (!touched.city || !form.city?.trim())) ||
      (pincodeState && (!touched.state || !form.state?.trim()))
    ) {
      setForm((prev) => ({
        ...prev,
        ...((pincodeCity && (!touched.city || !prev.city?.trim())) ? { city: pincodeCity } : {}),
        ...((pincodeState && (!touched.state || !prev.state?.trim())) ? { state: pincodeState } : {}),
      }));
    }
  }, [pincodeCity, pincodeState, touched.city, touched.state, form.city, form.state]);

  useEffect(() => {
    if (!/^\d{6}$/.test(String(form.pincode || '').trim())) {
      setForm((prev) => prev.city || prev.state ? { ...prev, city: '', state: '' } : prev);
    }
  }, [form.pincode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let clean = value;
    if (name === "pincode") clean = value.replace(/\D/g, "").slice(0, 6);
    else if (name === "mobile") clean = value.replace(/\D/g, "").slice(0, 10);
    else if (name === "city" || name === "state")
      clean = value.replace(/[^a-zA-Z\s]/g, "");
    else if (name === "name") clean = value.replace(/[^a-zA-Z\s]/g, "");
    else if (name === "address") clean = value.slice(0, MAX_ADDRESS_LENGTH);
    else if (name === "gstin") clean = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 15);

    const final = type === "checkbox" ? checked : clean;
    setForm((p) => name === 'pincode' && clean.length !== 6
      ? { ...p, pincode: final, city: '', state: '' }
      : { ...p, [name]: final });
    if (name === "city" || name === "state")
      setTouched((p) => ({ ...p, [name]: true }));

    if (touched[name]) {
      setErrors((p) => ({ ...p, [name]: validateAddr(name, final) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((p) => ({ ...p, [name]: true }));
    setErrors((p) => ({ ...p, [name]: validateAddr(name, value) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    const newTouched = {};

    ADDR_REQUIRED.forEach((field) => {
      newTouched[field] = true;
      const err = validateAddr(field, form[field] ?? "");
      if (err) newErrors[field] = err;
    });

    if (showCoordinates) {
      if (form.latitude && isNaN(parseFloat(form.latitude))) {
        newErrors.latitude = "Invalid latitude";
      }
      if (form.longitude && isNaN(parseFloat(form.longitude))) {
        newErrors.longitude = "Invalid longitude";
      }
    }

    setTouched((p) => ({ ...p, ...newTouched }));
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;
    onSubmit(form);
  };

  const inputClass = (name) =>
    `w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none transition-all focus:ring-2 ${
      errors[name] && touched[name]
        ? "border-red-400 bg-red-50/20 focus:border-red-500 focus:ring-red-100"
        : "border-gray-200 bg-white focus:border-[#046a38] focus:ring-emerald-100"
    }`;

  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/60 p-3 sm:p-6 backdrop-blur-sm transition-opacity"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-address-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
    >
      <div className="flex min-h-full items-center justify-center">
        <form
          onSubmit={handleSubmit}
          onClick={(e) => e.stopPropagation()}
          className="relative my-auto flex w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white text-left shadow-2xl transition-all"
          style={{ maxHeight: "calc(100vh - 2rem)" }}
        >
          {/* ── Modal Header (Sticky) ── */}
          <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 py-4 sm:px-6">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-[#046a38]">
                <FaMapMarkerAlt className="h-4 w-4" />
              </div>
              <div>
                <h2
                  id="edit-address-modal-title"
                  className="text-base sm:text-lg font-bold text-gray-900"
                >
                  Edit Address
                </h2>
                <p className="text-xs text-gray-500">
                  Update your delivery details below
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 active:scale-95 disabled:opacity-50"
              aria-label="Close dialog"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>

          {/* ── Modal Body (Scrollable) ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6 space-y-4">
            {/* Name & Mobile (2 cols) */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass("name")}
                  placeholder="e.g. Rahul Sharma"
                />
                {errors.name && touched.name && (
                  <p className="mt-1 text-xs text-red-500 font-medium">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                    +91
                  </span>
                  <input
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`${inputClass("mobile")} pl-11`}
                    placeholder="9876543210"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                  />
                </div>
                {errors.mobile && touched.mobile && (
                  <p className="mt-1 text-xs text-red-500 font-medium">
                    {errors.mobile}
                  </p>
                )}
              </div>
            </div>

            {/* Email (Full width) */}
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("email")}
                placeholder="name@example.com"
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                GSTIN (Optional)
              </label>
              <input
                name="gstin"
                value={form.gstin || ""}
                onChange={handleChange}
                maxLength={15}
                autoCapitalize="characters"
                className={inputClass("gstin")}
                placeholder="For a GST invoice"
              />
            </div>

            {/* Address Type (Home / Work / Other) */}
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600">
                Address Type
              </label>
              <div className="flex gap-2">
                {ADDRESS_TYPES.map((type) => {
                  const isSelected = form.address_type === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setForm((p) => ({ ...p, address_type: type }))
                      }
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-bold transition-all ${
                        isSelected
                          ? "border-[#046a38] bg-emerald-50 text-[#046a38] shadow-sm ring-1 ring-[#046a38]"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {isSelected && <FaCheck className="h-3 w-3" />}
                      <span>{type}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Street / Flat Address (Max 100 characters) */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Street Address / Flat / Building{" "}
                  <span className="text-red-500">*</span>
                </label>
                <span
                  className={`text-[11px] font-semibold ${
                    (form.address?.length || 0) >= MAX_ADDRESS_LENGTH
                      ? "text-red-500"
                      : "text-gray-400"
                  }`}
                >
                  {form.address?.length || 0}/{MAX_ADDRESS_LENGTH} chars
                </span>
              </div>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={2}
                maxLength={MAX_ADDRESS_LENGTH}
                className={`${inputClass("address")} resize-none`}
                placeholder="Flat 101, Green Heights"
              />
              {errors.address && touched.address && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {errors.address}
                </p>
              )}
            </div>

            {/* Pincode, City, State (3 cols) */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  name="pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass("pincode")}
                  placeholder="411001"
                  maxLength={6}
                />
                {errors.pincode && touched.pincode && (
                  <p className="mt-1 text-xs text-red-500 font-medium">
                    {errors.pincode}
                  </p>
                )}
                {pincodeStatus === "serviceable" && !errors.pincode && (
                  <p className="mt-1 text-[11px] font-semibold text-emerald-600">
                    ✓ Delivery available
                  </p>
                )}
                {pincodeStatus === "not_serviceable" && (
                  <p className="mt-1 text-[11px] font-semibold text-amber-600">
                    ⚠️ Limited delivery
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass("city")}
                  placeholder="Pune"
                />
                {errors.city && touched.city && (
                  <p className="mt-1 text-xs text-red-500 font-medium">
                    {errors.city}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-600">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass("state")}
                  placeholder="Maharashtra"
                />
                {errors.state && touched.state && (
                  <p className="mt-1 text-xs text-red-500 font-medium">
                    {errors.state}
                  </p>
                )}
              </div>
            </div>

            {/* Optional Coordinates Toggle */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
              <button
                type="button"
                onClick={() => setShowCoordinates((prev) => !prev)}
                className="flex w-full items-center justify-between text-xs font-bold text-gray-600 hover:text-gray-900"
              >
                <span>GPS Coordinates (Optional)</span>
                <span className="text-[11px] text-[#046a38]">
                  {showCoordinates ? "Hide −" : "Show +"}
                </span>
              </button>

              {showCoordinates && (
                <div className="mt-3 grid grid-cols-2 gap-3 pt-2 border-t border-gray-200/60">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold text-gray-500">
                      Latitude
                    </label>
                    <input
                      name="latitude"
                      type="number"
                      step="any"
                      value={form.latitude}
                      onChange={handleChange}
                      className={inputClass("latitude")}
                      placeholder="18.5204"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold text-gray-500">
                      Longitude
                    </label>
                    <input
                      name="longitude"
                      type="number"
                      step="any"
                      value={form.longitude}
                      onChange={handleChange}
                      className={inputClass("longitude")}
                      placeholder="73.8567"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Make Default Checkbox */}
            <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-gray-100 bg-gray-50/50 p-3 text-xs sm:text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
              <input
                name="is_default"
                type="checkbox"
                checked={form.is_default}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-[#046a38] focus:ring-[#046a38]"
              />
              <span>Set as my default delivery address</span>
            </label>
          </div>

          {/* ── Modal Footer (Sticky) ── */}
          <div className="sticky bottom-0 z-20 flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/90 px-5 py-3.5 backdrop-blur-sm sm:px-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-xs sm:text-sm font-bold text-gray-700 transition hover:bg-gray-50 active:scale-95 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#046a38] px-6 py-2.5 text-xs sm:text-sm font-bold text-white shadow-md transition hover:bg-[#03522b] active:scale-95 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <FaSpinner className="h-3.5 w-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Update Address</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutEditModal;
